import React, { useContext } from "react";
import { Navigate } from "react-router-dom";
import Landing from "./Landing";
import TokenService from "../../services/token.service";
import { UserContext } from "../../utils/contexts/UserContext";
import InlineSpinner from "../../components/Skeleton/InlineSpinner";

/**
 * Decides what `/` shows.
 *
 * - **No access token in localStorage** → the landing page, immediately. This
 *   is the anonymous-visitor path, and it is a synchronous check on purpose:
 *   `useFindUser` fires `GET /auth/me/` for every visitor, so gating on its
 *   `isLoading` would show a spinner until that request 401s and hang for the
 *   full timeout whenever the backend is down. The marketing page must render
 *   regardless of backend health.
 * - **Token present** → resolve who they are, then send them to the app. The
 *   protected tree owns `/home`; role guards there redirect teachers and
 *   students onward to their own landing paths.
 * - **Token present but invalid** → the landing page, since they are not
 *   actually signed in.
 *
 * `/login` is untouched, so ProtectedRoute still sends an expired session to
 * the login form rather than to marketing copy.
 */
export default function RootGate() {
  const { user, isLoading } = useContext(UserContext);

  if (!TokenService.getLocalAccessToken()) {
    return <Landing />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <InlineSpinner className="h-10 w-10 text-primary" label="Loading" />
      </div>
    );
  }

  return user?.uuid ? <Navigate to="/home" replace /> : <Landing />;
}
